import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Loader2, Copy, ArrowUpRight, Dog as DogIcon, CalendarDays, Clock, X } from "lucide-react";
import { toast } from "sonner";

const P = (title, prompt, type = "chat") => ({ title, prompt, type });

function getContext(pathname, role) {
  const seg = pathname.split("/").filter(Boolean);
  if (seg[0] === "dogs" && seg[1]) return {
    label: "Dog profile", dogId: seg[1],
    prompts: [P("Summarize this dog's progress", "Summarize this dog's progress.", "progress_summary"),
      P("Draft homework for this dog", "Draft HV-method homework for this dog.", "draft_homework"),
      P("Next training focus", "What should be the next training focus for this dog?", "next_step"),
      P("Explain behaviour notes", "Explain this dog's behaviour notes in plain language.", "summary")],
  };
  if (seg[0] === "classes" && seg[1]) return {
    label: "Class detail", classId: seg[1],
    prompts: [P("Prepare this class", "Help me prepare this class with focus points.", "summary"),
      P("Draft homework for this class", "Draft homework for the dogs in this class.", "draft_homework"),
      P("Summarize attendance", "Summarize attendance for this class.", "summary"),
      P("Which dogs need follow-up?", "Which dogs in this class need follow-up?", "risk")],
  };
  if (seg[0] === "academy" && seg[1] === "enrollments") return { label: "Academy course", prompts: null };
  const map = {
    payments: { label: "HV Payments", prompts: [P("Summarize unpaid bookings", "Summarize unpaid bookings.", "summary"), P("Payments needing attention", "Which payments need attention?", "risk"), P("Explain revenue by location", "Explain revenue by location.", "summary"), P("Royalty issues", "Show royalty issues.", "quality_insight")] },
    quality: { label: "HV Quality", prompts: [P("Summarize open quality issues", "Summarize open quality issues.", "quality_insight"), P("Which locations are critical?", "Which locations are critical on quality?", "risk"), P("What to fix first", "What should we fix first?", "next_step"), P("Draft an action plan", "Draft an action plan for the top quality issue.", "recommendation")] },
    academy: { label: "HV Academy", prompts: [P("Trainers needing certification", "Which trainers need certification?", "quality_insight"), P("Summarize academy progress", "Summarize academy progress.", "summary"), P("Who is overdue?", "Who is overdue on required courses?", "risk"), P("Suggest next course assignments", "Suggest next course assignments.", "next_step")] },
    analytics: { label: "HV Analytics", prompts: [P("Summarize performance", "Summarize the key performance highlights.", "summary"), P("Biggest risks", "What are the biggest risks in this data?", "risk"), P("Suggest priorities", "Suggest priorities based on this analytics.", "next_step")] },
  };
  if (map[seg[0]]) return map[seg[0]];
  if (!seg[0] || seg[0] === "") {
    if (["super_admin", "head_office_admin"].includes(role)) return { label: "Head Office dashboard", prompts: [P("Summarize network health", "Summarize network health.", "summary"), P("Which locations need attention?", "Which locations need attention?", "risk"), P("Trainer certification gaps", "Show trainer certification gaps.", "quality_insight"), P("Priorities this week", "Suggest priorities for this week.", "next_step")] };
    if (["franchise_owner", "location_manager"].includes(role)) return { label: "Location dashboard", prompts: [P("Summarize today's operations", "Summarize today's operations.", "summary"), P("Clients needing follow-up", "Which clients need follow-up?", "risk"), P("Classes missing homework", "Which classes are missing homework?", "recommendation"), P("Dogs needing a trainer", "Which dogs need trainer assignment?", "risk")] };
  }
  return { label: "Hundeverket", prompts: null };
}

export function AiAssistant() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [recent, setRecent] = useState([]);
  const scrollRef = useRef(null);
  const ctx = getContext(pathname, user?.role);

  useEffect(() => {
    if (ctx.prompts) setPrompts(ctx.prompts);
    else api.get("/ai/suggested-prompts").then((r) => setPrompts(r.data)).catch(() => setPrompts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => { if (open) api.get("/ai/recent").then((r) => setRecent(r.data)).catch(() => {}); }, [open]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const send = async (text, type = "chat") => {
    const prompt = (text ?? input).trim();
    if (!prompt || sending) return;
    setMessages((m) => [...m, { role: "user", text: prompt }]);
    setInput(""); setSending(true);
    try {
      const r = await api.post("/ai/chat", {
        prompt, interaction_type: type, related_dog_id: ctx.dogId || null,
        related_class_id: ctx.classId || null, page_context: ctx.label,
      });
      setMessages((m) => [...m, { role: "ai", text: r.data.response, type: r.data.type }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "HV AI could not respond. Please try again.";
      setMessages((m) => [...m, { role: "ai", text: msg, error: true }]);
    }
    setSending(false);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="topbar-ai-btn"
        className="flex items-center gap-1.5 rounded-full bg-neutral-900 text-primary-foreground pl-2.5 pr-3.5 py-1.5 text-sm font-medium hover:bg-neutral-800 transition-colors">
        <Sparkles size={15} className="text-amber-300" /> HV AI
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col gap-0" data-testid="ai-assistant-panel">
          <SheetHeader className="p-4 border-b border-border space-y-0 text-left">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-neutral-900 flex items-center justify-center shrink-0"><Sparkles size={18} className="text-amber-300" /></div>
              <div className="flex-1">
                <SheetTitle className="font-heading text-base">HV AI Assistant</SheetTitle>
                <p className="text-xs text-muted-foreground">Intelligent support for Hundeverket</p>
              </div>
            </div>
            <SheetDescription className="sr-only">Chat with HV AI, a role-aware assistant that helps with the current page.</SheetDescription>
            <div className="pt-2 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="ai-context-label">
              <Clock size={12} /> Context: <span className="font-medium text-foreground">{ctx.label}</span>
            </div>
          </SheetHeader>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="space-y-4">
                {recent.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Recent activity</div>
                    <div className="space-y-1.5">
                      {recent.slice(0, 4).map((r) => (
                        <div key={r.id} className="text-xs text-muted-foreground flex items-center justify-between gap-2 rounded-lg bg-muted/50 px-2.5 py-1.5" data-testid="ai-recent-item">
                          <span className="truncate">{r.prompt}</span>
                          <span className="capitalize shrink-0 text-[10px] pl-1">{String(r.interaction_type).replace(/_/g, " ")}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Suggested for this page</div>
                  <div className="flex flex-col gap-2">
                    {prompts.map((p, i) => (
                      <button key={i} onClick={() => send(p.prompt, p.type)} disabled={sending}
                        className="text-left text-sm rounded-lg border border-border/70 px-3 py-2 hover:bg-secondary/60 hover:border-primary/40 transition-colors disabled:opacity-50" data-testid="ai-suggested-prompt">
                        {p.title}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}`}>
                <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : m.error ? "bg-red-50 text-red-800 border border-red-200" : "bg-secondary text-secondary-foreground"}`}>
                  {m.role === "ai" ? (
                    <>
                      <div className="prose prose-sm max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5"><ReactMarkdown>{m.text}</ReactMarkdown></div>
                      {!m.error && (
                        <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-border/60">
                          <Qa icon={Copy} label="Copy" onClick={() => { navigator.clipboard?.writeText(m.text); toast.success("Copied"); }} testid="ai-qa-copy" />
                          {m.type === "draft_homework" && <Qa icon={Sparkles} label="Use homework" onClick={() => toast.info("Homework draft ready — create it from the dog/class page (coming soon).")} testid="ai-qa-homework" />}
                          {ctx.dogId && <Qa icon={DogIcon} label="Open dog" onClick={() => { setOpen(false); navigate(`/dogs/${ctx.dogId}`); }} testid="ai-qa-open-dog" />}
                          {ctx.classId && <Qa icon={CalendarDays} label="Open class" onClick={() => { setOpen(false); navigate(`/classes/${ctx.classId}`); }} testid="ai-qa-open-class" />}
                        </div>
                      )}
                    </>
                  ) : m.text}
                </div>
              </div>
            ))}
            {sending && <div className="flex justify-start"><div className="rounded-2xl px-3.5 py-2.5 bg-secondary text-muted-foreground text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> HV AI is thinking…</div></div>}
          </div>

          <div className="border-t border-border p-3">
            {messages.length > 0 && <button onClick={() => setMessages([])} className="text-xs text-muted-foreground hover:text-foreground mb-2 flex items-center gap-1" data-testid="ai-new-chat"><X size={12} /> New conversation</button>}
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
              <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask HV AI…" data-testid="ai-input" />
              <Button type="submit" size="icon" disabled={sending || !input.trim()} data-testid="ai-send"><Send size={15} /></Button>
            </form>
            <p className="text-[10px] text-muted-foreground mt-2">HV AI supports but doesn't replace professional trainer judgement. Contact a trainer for behaviour concerns.</p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

const Qa = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="inline-flex items-center gap-1 text-xs rounded-full border border-border bg-card px-2.5 py-1 hover:bg-secondary/60 transition-colors">
    <Icon size={12} /> {label}
  </button>
);
